<script setup>
import { defineProps, computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import Message from './Message.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  loadingMessage: {
    type: String,
    default: '',
  },
  emptyMessage: {
    type: String,
    default: '',
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['retry']);
const { t, locale } = useI18n();

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: ['content_attributes.translations'],
  });
});

const currentChat = useMapGetter('getSelectedChat');

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

const normalizedLocale = computed(() =>
  (locale.value || 'pt_BR').replace('_', '-')
);

const dateFormatter = computed(
  () =>
    new Intl.DateTimeFormat(normalizedLocale.value, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
);

const loadingStateMessage = computed(
  () => props.loadingMessage || t('CONVERSATION.MESSAGE_LIST.LOADING')
);

const emptyStateMessage = computed(
  () => props.emptyMessage || t('CONVERSATION.MESSAGE_LIST.EMPTY')
);

const errorStateMessage = computed(
  () => props.errorMessage || t('CONVERSATION.MESSAGE_LIST.ERROR')
);

const unreadDividerLabel = computed(() =>
  t('CONVERSATION.MESSAGE_LIST.UNREAD_DIVIDER')
);

const shouldShowMessages = computed(
  () => !props.isLoading && !props.hasError && allMessages.value.length > 0
);

const toMessageDate = timestamp => {
  if (!timestamp) return null;

  const numericTimestamp = Number(timestamp);
  if (!Number.isFinite(numericTimestamp)) return null;

  const date = new Date(
    numericTimestamp > 10000000000 ? numericTimestamp : numericTimestamp * 1000
  );

  return Number.isNaN(date.getTime()) ? null : date;
};

const isSameCalendarDay = (dateA, dateB) => {
  if (!dateA || !dateB) return false;

  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
};

const dateWithDayOffset = offset => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offset);
  return date;
};

const shouldShowDateSeparator = index => {
  const currentDate = toMessageDate(allMessages.value[index]?.createdAt);
  if (!currentDate) return false;
  if (index === 0) return true;

  const previousDate = toMessageDate(allMessages.value[index - 1]?.createdAt);
  return !isSameCalendarDay(currentDate, previousDate);
};

const dateSeparatorLabel = timestamp => {
  const date = toMessageDate(timestamp);
  if (!date) return '';

  if (isSameCalendarDay(date, dateWithDayOffset(0))) {
    return t('CONVERSATION.MESSAGE_LIST.TODAY');
  }

  if (isSameCalendarDay(date, dateWithDayOffset(-1))) {
    return t('CONVERSATION.MESSAGE_LIST.YESTERDAY');
  }

  return dateFormatter.value.format(date);
};

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};
</script>

<template>
  <ul class="w-full bg-white px-4 py-5">
    <slot name="beforeAll" />
    <li
      v-if="isLoading"
      class="flex list-none justify-center px-3 py-8 text-center text-sm text-lais-text-muted"
      role="status"
    >
      {{ loadingStateMessage }}
    </li>
    <li
      v-else-if="hasError"
      class="flex list-none justify-center px-3 py-8 text-center text-sm text-lais-danger"
      role="alert"
    >
      {{ errorStateMessage }}
    </li>
    <li
      v-else-if="!allMessages.length"
      class="flex list-none justify-center px-3 py-8 text-center text-sm text-lais-text-muted"
    >
      {{ emptyStateMessage }}
    </li>
    <template v-if="shouldShowMessages">
      <template v-for="(message, index) in allMessages" :key="message.id">
        <li
          v-if="shouldShowDateSeparator(index)"
          class="my-3 flex list-none items-center justify-center"
          role="separator"
        >
          <span
            class="rounded-full border border-lais-border bg-white px-3 py-1 text-[11px] font-semibold text-lais-text-muted shadow-sm"
          >
            {{ dateSeparatorLabel(message.createdAt) }}
          </span>
        </li>
        <slot
          v-if="firstUnreadId && message.id === firstUnreadId"
          name="unreadBadge"
        >
          <li class="my-3 flex list-none items-center justify-center">
            <span
              class="rounded-full bg-lais-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm"
            >
              {{ unreadDividerLabel }}
            </span>
          </li>
        </slot>
        <Message
          v-bind="message"
          :is-email-inbox="isAnEmailChannel"
          :in-reply-to="getInReplyToMessage(message)"
          :group-with-next="shouldGroupWithNext(index, allMessages)"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :current-user-id="currentUserId"
          data-clarity-mask="True"
          @retry="emit('retry', message)"
        />
      </template>
    </template>
    <slot name="after" />
  </ul>
</template>
